/**
 * Backend API Service for Popup Alerts
 * Handles API calls for popup alert configuration (per-store)
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL!;

/**
 * Popup alert data structure
 */
export interface PopupAlertData {
  message: string;
  isEnabled: boolean;
  lastEdited: Date | null;
  lastAuthor: string;
}

/**
 * Get Firebase ID token from current user
 * Requires Firebase Auth to be initialized and user logged in
 */
const getAuthToken = async (): Promise<string> => {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('No authenticated user found');
  return await auth.currentUser.getIdToken();
};

/**
 * Get popup alert configuration for a store (admin only)
 */
export const getPopupAlert = async (storeId: string): Promise<PopupAlertData | null> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/popup-alerts/${storeId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }

    if (result.success && result.data) {
      return {
        message: result.data.message,
        isEnabled: result.data.isEnabled,
        lastEdited: result.data.lastEdited ? new Date(result.data.lastEdited) : null,
        lastAuthor: result.data.lastAuthor || '',
      };
    }

    return null;
  } catch (error) {
    console.error('Error fetching popup alert:', error);
    throw error;
  }
};

/**
 * Save popup alert configuration for a store (admin only)
 */
export const savePopupAlert = async (storeId: string, data: {
  message: string;
  isEnabled: boolean;
}): Promise<void> => {
  try {
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/api/popup-alerts/${storeId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }
  } catch (error) {
    console.error('Error saving popup alert:', error);
    throw error;
  }
};

/**
 * Get popup alert for a specific store (public, for page display)
 */
export const getPopupAlertForStore = async (storeId: string): Promise<{
  message: string;
} | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/popup-alerts/store/${storeId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const result = await response.json();

    if (result.success && result.data) {
      return result.data;
    }

    return null;
  } catch (error) {
    console.error('Error fetching popup alert for store:', error);
    return null;
  }
};
